import { clsx } from 'clsx'
import { Flag } from '@/shared/components/ui/Flag'
import { Badge } from '@/shared/components/ui/Badge'
import { Button } from '@/shared/components/ui/Button'
import { Section } from '@/shared/components/ui/Section'
import { Skeleton } from '@/shared/components/ui/Skeleton'
import { Tabs } from '@/shared/components/ui/Tabs'
import { showsCountryFlag } from '@/shared/lib/teamType'
import { formatKickoffTime, formatShortDate, isSameMatchDay } from '@/shared/lib/formatDate'
import { useTranslation } from '@/shared/i18n/useTranslation'
import type { TranslateFn } from '@/shared/i18n/useTranslation'
import type { Language } from '@/shared/i18n/types'
import { useCompetitionFixtures } from '../queries'
import type { CompetitionTab } from '../lib/survivorHome'
import { isLiveStatus, isRoundLive, matchTimeState, roundPhaseKey } from '../lib/roundDisplay'
import type { CompetitionFixtures, CompetitionFormat, MatchFixtureDto, RoundDto } from '../types'
import styles from './FixtureWidget.module.css'

type FixtureWidgetProps = {
  competitions:          CompetitionTab[]
  selectedCompetitionId: string
  onSelectCompetition:   (id: string) => void
  /** The team the caller picked this round, marked in the match list. */
  pickedTeamId?:         string
}

// ── Football match row ────────────────────────────────────────────────────────

type MatchRowProps = {
  match:         MatchFixtureDto
  format:        CompetitionFormat
  pickedTeamId?: string
}

function FootballMatchRow({ match, format, pickedTeamId }: MatchRowProps) {
  const { t, language } = useTranslation()

  const home     = match.homeTeam
  const away     = match.awayTeam
  const homeName = home?.name ?? match.homeTeamPlaceholder ?? '?'
  const awayName = away?.name ?? match.awayTeamPlaceholder ?? '?'
  const isLive   = isLiveStatus(match.status)
  const hasScore = match.homeScore !== null && match.awayScore !== null

  // Gated per team, not per competition: a club cup is KNOCKOUT but its teams are
  // still clubs. See shared/lib/teamType — "CAM" resolves to Cameroon otherwise.
  const showHomeFlag = showsCountryFlag(home?.teamType)
  const showAwayFlag = showsCountryFlag(away?.teamType)

  function isMyPick(teamId: string | undefined): boolean {
    return pickedTeamId !== undefined && teamId === pickedTeamId
  }

  const isMine = isMyPick(home?.id) || isMyPick(away?.id)

  return (
    <div className={clsx(styles.matchRow, isMine && styles.isMine)}>
      <span className={clsx(styles.matchTime, isLive && styles.isLive)}>
        {timeLabel(match, t, language)}
      </span>

      <div className={styles.matchTeamHome}>
        <span className={clsx(styles.matchName, isMyPick(home?.id) && styles.isPicked)}>
          {homeName}
        </span>
        {showHomeFlag && home && <Flag code={home.code} title={home.name} size={18} />}
      </div>

      <div className={styles.matchVsBlock}>
        {hasScore ? (
          <span className={clsx(styles.matchScore, isLive && styles.isLive)}>
            {match.homeScore}–{match.awayScore}
          </span>
        ) : (
          <span className={styles.matchVs}>{t('fixture.vs')}</span>
        )}
      </div>

      <div className={styles.matchTeamAway}>
        {showAwayFlag && away && <Flag code={away.code} title={away.name} size={18} />}
        <span className={clsx(styles.matchName, isMyPick(away?.id) && styles.isPicked)}>
          {awayName}
        </span>
      </div>

      <span className={styles.matchGroup}>
        {/* Group codes are a group-stage concept; a league round has none. */}
        {format === 'KNOCKOUT' && match.groupCode
          ? t('fixture.group', { code: match.groupCode })
          : ''}
      </span>
    </div>
  )
}

function timeLabel(match: MatchFixtureDto, t: TranslateFn, language: Language): string {
  const state = matchTimeState(match)
  if (state === 'postponed') return t('fixture.postponed')
  if (state === 'live')      return t('fixture.live')
  return formatKickoffTime(match.kickoffAt, language)
}

// ── CS2 match row ─────────────────────────────────────────────────────────────

function Cs2MatchRow({ match }: MatchRowProps) {
  const { t, language } = useTranslation()

  const homeName  = match.homeTeam?.name ?? match.homeTeamPlaceholder ?? '?'
  const awayName  = match.awayTeam?.name ?? match.awayTeamPlaceholder ?? '?'
  const homeScore = match.homeScore
  const awayScore = match.awayScore

  return (
    <div className={styles.matchRow}>
      <span className={styles.matchTime}>{formatKickoffTime(match.kickoffAt, language)}</span>
      <div className={styles.matchTeamHome}>
        <span className={styles.matchName}>{homeName}</span>
      </div>
      <div className={styles.matchVsBlock}>
        {homeScore !== null && awayScore !== null ? (
          <span className={styles.matchScore}>{homeScore}–{awayScore}</span>
        ) : (
          <span className={styles.matchVs}>{t('fixture.vs')}</span>
        )}
      </div>
      <div className={styles.matchTeamAway}>
        <span className={styles.matchName}>{awayName}</span>
      </div>
      <span className={styles.matchGroup} />
    </div>
  )
}

// ── Round ─────────────────────────────────────────────────────────────────────

function RoundRows({
  round,
  fixtures,
  pickedTeamId,
}: {
  round:         RoundDto
  fixtures:      CompetitionFixtures
  pickedTeamId?: string
}) {
  const Row = fixtures.sport === 'FOOTBALL' ? FootballMatchRow : Cs2MatchRow

  return (
    <>
      {round.matches.map((m) => (
        <Row
          key={m.id}
          match={m}
          format={fixtures.format}
          {...(pickedTeamId !== undefined ? { pickedTeamId } : {})}
        />
      ))}
    </>
  )
}

/** "Round 3 · Group stage" */
function roundTitle(round: RoundDto, t: TranslateFn): string {
  const phase = roundPhaseKey(round)
  const base  = t('survivor.round', { number: round.number })
  return phase ? `${base} · ${t(phase)}` : base
}

/** "Today · 8 matches" */
function roundMeta(round: RoundDto, t: TranslateFn, language: Language): string {
  const date = new Date(round.firstMatchAt)
  const when = isSameMatchDay(date, new Date())
    ? t('fixture.today')
    : formatShortDate(round.firstMatchAt, language)

  return `${when} · ${t('fixture.count', { count: round.matches.length })}`
}

// ── Main component ────────────────────────────────────────────────────────────

export function FixtureWidget({
  competitions,
  selectedCompetitionId,
  onSelectCompetition,
  pickedTeamId,
}: FixtureWidgetProps) {
  const { t, language } = useTranslation()
  const { data: fixtures, isPending, isError, refetch } =
    useCompetitionFixtures(selectedCompetitionId)

  const current  = fixtures?.currentRound
  const previous = fixtures?.previousRound

  return (
    <div className={styles.widget}>
      {competitions.length > 1 && (
        <Tabs
          items={competitions.map((c) => ({ id: c.id, label: c.name }))}
          activeId={selectedCompetitionId}
          onSelect={onSelectCompetition}
          label={t('nav.survivor')}
        />
      )}

      {isPending ? (
        <div className={styles.skeleton}>
          <Skeleton height="18px" />
          <Skeleton height="18px" />
          <Skeleton height="18px" />
        </div>
      ) : isError ? (
        <div className={styles.empty}>
          <span className={styles.emptyText}>{t('fixture.error')}</span>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : !fixtures || (!current && !previous) ? (
        <div className={styles.empty}>
          <span className={styles.emptyText}>{t('fixture.empty')}</span>
        </div>
      ) : (
        <>
          {current && (
            <Section
              label={roundTitle(current, t)}
              meta={
                isRoundLive(current) ? (
                  <Badge tone="live" variant="soft" hasDot>{t('fixture.live')}</Badge>
                ) : (
                  roundMeta(current, t, language)
                )
              }
            >
              <RoundRows
                round={current}
                fixtures={fixtures}
                {...(pickedTeamId !== undefined ? { pickedTeamId } : {})}
              />
            </Section>
          )}

          {previous && (
            <Section
              isSubtle
              label={t('fixture.results', { number: previous.number })}
              meta={roundMeta(previous, t, language)}
            >
              <RoundRows round={previous} fixtures={fixtures} />
            </Section>
          )}
        </>
      )}
    </div>
  )
}
